import {AxiosProgressEvent} from "axios"
import {ApiResponse} from "../api"
import Utils from "../utils";

export interface FileUtilsInitialFileUploadArg {
    chunk: Blob,
    fileSize: number,
    fileName: string,
    onUploadProgress: ((event: AxiosProgressEvent) => void),
    abortController?: AbortController,
}

export interface FileUtilsUploadFileResponse {
}

export interface FileUtilsInitialFileUploadCallback<T extends FileUtilsUploadFileResponse = FileUtilsUploadFileResponse> {
    (arg: FileUtilsInitialFileUploadArg): ApiResponse<T>
}

export interface FileUtilsUploadRemainingChunksArg {
    nextChunk: Blob,
    nextChunkFileName: string,
    fileId: string,
    onUploadProgress: ((event: AxiosProgressEvent) => void),
    abortController?: AbortController,
}

export interface FileUtilsUploadRemainingChunksCallback<T> {
    (arg: FileUtilsUploadRemainingChunksArg): ApiResponse<T>
}

export interface FileUtilsOnProgressCallback {
    (progress: number): void
}

/**
 * The file utilities.
 */
class FileUtils {

    /**
     * The file chunk size.
     * @default 4MB
     */
    public static readonly fileChunkSize = 1024 * 1024 * 4;

    /**
     * The maximum upload file size.
     * @default 100MB
     */
    public static readonly maximumUploadFileSize = 1024 * 1024 * 100;


    /**
     * Uploads the remaining chunk of the file.
     *
     * @param file                  The file to upload.
     * @param initialFileUpload     The initial file upload function.
     * @param uploadRemainingChunk  The function that uploads the remaining chunks.
     * @param getFileGuid           The function that gets the file GUID.
     * @param onProgress            The progress event handler.
     * @param abortController       The abort controller to cancel the upload.
     * @returns                     The response.
     */
    public static async uploadFile<T extends FileUtilsUploadFileResponse, R>(
        file: File,
        initialFileUpload: FileUtilsInitialFileUploadCallback<T>,
        uploadRemainingChunk: FileUtilsUploadRemainingChunksCallback<R>,
        getFileGuid: (response: T) => string,
        onProgress: FileUtilsOnProgressCallback,
        abortController?: AbortController
    ): Promise<T | Error> {
        const chunkSize = this.fileChunkSize;
        const totalChunks = Math.ceil(file.size / chunkSize);
        let chunkIndex = 0;
        let result: T | undefined = undefined;

        if (file.size > this.maximumUploadFileSize) {
            return new Error(`The file size exceeds the maximum upload file size of ${Utils.formatBytes(this.maximumUploadFileSize)}.`);
        }

        onProgress(0);

        /**
         * The progress event handler for the file upload.
         * @param event
         */
        const onUploadProgress = (event: AxiosProgressEvent) => {
            const percentageDoneInThisIteration = ((event.loaded / (event.total ?? 1)) * 100);
            const previousDone = (chunkIndex / totalChunks) * 100;
            const progress = previousDone + (percentageDoneInThisIteration / totalChunks);
            onProgress(progress);
        }

        for (chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const chunk = file.slice(chunkIndex * chunkSize, (chunkIndex + 1) * chunkSize);
            if (chunkIndex === 0) {
                if (abortController?.signal.aborted) {
                    return new Error("The upload was cancelled.");
                }
                const response = await initialFileUpload({
                    chunk: chunk,
                    fileName: file.name,
                    fileSize: file.size,
                    onUploadProgress,
                    abortController,
                });
                if (!response?.resultFlag) {
                    return new Error(response?.message ?? "An error occurred while uploading the file.");
                }
                result = response?.data;
            } else {
                if (abortController?.signal.aborted) {
                    return new Error("The upload was cancelled.");
                }
                const response = await uploadRemainingChunk({
                    nextChunk: chunk,
                    nextChunkFileName: file.name,
                    fileId: getFileGuid(result!),
                    onUploadProgress,
                    abortController,
                });
                if (!response?.resultFlag) {
                    return new Error(response?.message ?? "An error occurred while uploading the file.");
                }
            }
        }

        if (!result) {
            return new Error("An error occurred while uploading the file.");
        }

        return result;
    }

}


export default FileUtils;
